import { useEffect, useState } from 'react';
import { motion } from 'motion/react';
import { ArrowLeft, Users, MessageSquare, DollarSign, TrendingUp, Activity } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { api } from '@/services/api';
import type { AdminStats } from '@/types';
import { lightImpact } from '@/utils/haptics';

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState<AdminStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadStats = async () => {
      try {
        const data = await api.getAdminDashboard();
        setStats(data);
      } catch (err) {
        setError('Failed to load dashboard data');
      } finally {
        setLoading(false);
      }
    };
    loadStats();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <motion.div
          animate={{ rotate: 360 }}
          transition={{ repeat: Infinity, duration: 1, ease: 'linear' }}
          className="w-8 h-8 border-3 border-primary-600 border-t-transparent rounded-full"
          style={{ borderWidth: '3px' }}
        />
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 text-center text-red-500 font-medium">
        {error}
      </div>
    );
  }

  const statCards = [
    { label: 'Total Users', value: stats?.totalUsers || 0, icon: Users, color: 'from-blue-500 to-blue-600', trend: '+12%' },
    { label: 'Conversations', value: stats?.totalConversations || 0, icon: MessageSquare, color: 'from-purple-500 to-purple-600', trend: '+8%' },
    { label: 'Daily Cost', value: `$${(stats?.dailyUsage?.[stats.dailyUsage.length - 1]?.cost || 0).toFixed(2)}`, icon: DollarSign, color: 'from-emerald-500 to-emerald-600', trend: '-3%' },
    { label: 'Active Now', value: '3', icon: Activity, color: 'from-amber-500 to-amber-600', trend: 'Live' },
  ];

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <motion.button
          whileTap={{ scale: 0.9 }}
          onClick={async () => { await lightImpact(); navigate('/'); }}
          className="p-3 rounded-xl hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors min-h-12 min-w-12 flex items-center justify-center"
        >
          <ArrowLeft className="w-5 h-5" />
        </motion.button>
        <h1 className="text-xl font-bold text-slate-900 dark:text-white tracking-tight">Admin Dashboard</h1>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 gap-3">
        {statCards.map((card, index) => (
          <motion.div
            key={card.label}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.1 }}
            className="p-4 rounded-2xl bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 shadow-card"
          >
            <div className="flex items-center justify-between mb-3">
              <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${card.color} flex items-center justify-center shadow-lg`}>
                <card.icon className="w-5 h-5 text-white" />
              </div>
              <span className={`text-xs font-bold ${card.trend.startsWith('+') ? 'text-emerald-500' : card.trend.startsWith('-') ? 'text-red-500' : 'text-primary-500'}`}>
                {card.trend}
              </span>
            </div>
            <p className="text-2xl font-bold text-slate-900 dark:text-white">{card.value}</p>
            <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">{card.label}</p>
          </motion.div>
        ))}
      </div>

      {/* Daily Usage */}
      {stats?.dailyUsage && stats.dailyUsage.length > 0 && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="p-5 rounded-2xl bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 shadow-card"
        >
          <h3 className="text-sm font-bold text-slate-700 dark:text-slate-200 mb-4 flex items-center gap-2">
            <TrendingUp className="w-4 h-4 text-primary-600" />
            Daily Usage
          </h3>
          {/* Simple bar chart */}
          <div className="flex items-end gap-2 h-32">
            {stats.dailyUsage.slice(-7).map((day, i) => {
              const maxCost = Math.max(...stats.dailyUsage.map((d) => d.cost), 1);
              const height = (day.cost / maxCost) * 100;
              return (
                <div key={day.date} className="flex-1 flex flex-col items-center gap-1">
                  <motion.div
                    initial={{ height: 0 }}
                    animate={{ height: `${height}%` }}
                    transition={{ delay: 0.5 + i * 0.1, duration: 0.5 }}
                    className="w-full max-w-[40px] rounded-t-lg bg-gradient-to-t from-primary-600 to-primary-400"
                  />
                  <span className="text-[10px] text-slate-400">{day.date.slice(5)}</span>
                </div>
              );
            })}
          </div>
        </motion.div>
      )}

      {/* Top Users */}
      {stats?.topUsers && stats.topUsers.length > 0 && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="p-5 rounded-2xl bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 shadow-card"
        >
          <h3 className="text-sm font-bold text-slate-700 dark:text-slate-200 mb-4">Top Users</h3>
          <div className="space-y-2">
            {stats.topUsers.map((user, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.6 + index * 0.05 }}
                className="flex items-center justify-between py-2.5 border-b border-slate-100 dark:border-slate-700 last:border-0"
              >
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-full bg-gradient-to-br from-primary-500 to-purple-500 flex items-center justify-center text-white text-xs font-bold">
                    {user.name.charAt(0)}
                  </div>
                  <span className="text-sm font-medium text-slate-700 dark:text-slate-200">{user.name}</span>
                </div>
                <span className="text-sm font-bold text-slate-900 dark:text-white">{user.usage.toLocaleString()}</span>
              </motion.div>
            ))}
          </div>
        </motion.div>
      )}
    </div>
  );
};

export default AdminDashboard;
